import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Gift,
  Plus,
  Trash2,
  RefreshCw,
  BarChart3,
  Users,
  Euro,
  TrendingUp,
  CheckCircle,
  X,
  Heart,
  Bus,
  UtensilsCrossed,
  ShieldCheck,
  Dumbbell,
  Package,
} from "lucide-react";
import { apiClient } from "../services/api";
import i18n from "../i18n";

// ─── helpers ────────────────────────────────────────────────────────────────

const COUNTRY_CURRENCY = { FR: "EUR", TN: "TND", QA: "QAR", MA: "MAD", SA: "SAR" };

const CATEGORY_ICONS = {
  health: Heart,
  pension: ShieldCheck,
  meal_vouchers: UtensilsCrossed,
  transport: Bus,
  life_insurance: ShieldCheck,
  gym: Dumbbell,
  other: Package,
};

const CATEGORY_COLORS = {
  health: "text-red-500 bg-red-100 dark:bg-red-900/30",
  pension: "text-blue-500 bg-blue-100 dark:bg-blue-900/30",
  meal_vouchers: "text-orange-500 bg-orange-100 dark:bg-orange-900/30",
  transport: "text-green-500 bg-green-100 dark:bg-green-900/30",
  life_insurance: "text-purple-500 bg-purple-100 dark:bg-purple-900/30",
  gym: "text-teal-500 bg-teal-100 dark:bg-teal-900/30",
  other: "text-gray-500 bg-gray-100 dark:bg-gray-800",
};

const CATEGORIES = [
  { value: "health" },
  { value: "pension" },
  { value: "meal_vouchers" },
  { value: "transport" },
  { value: "life_insurance" },
  { value: "gym" },
  { value: "other" },
];

const categoryLabel = (category, t, fallback) =>
  t(`benefitsHub.categories.${category}`, { defaultValue: fallback || category });

const CategoryIcon = ({ category, size = 16 }) => {
  const Icon = CATEGORY_ICONS[category] || Package;
  const colorClass = CATEGORY_COLORS[category] || CATEGORY_COLORS.other;
  return (
    <div className={`p-1.5 rounded-lg ${colorClass}`}>
      <Icon size={size} />
    </div>
  );
};

const fmt = (n) => n?.toLocaleString(i18n.language, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ?? "—";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(i18n.language) : "—");

const KpiCard = ({ title, value, sub, icon: Icon, color = "blue" }) => {
  const colors = {
    blue: "text-blue-600 bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400",
    green: "text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-400",
    orange: "text-orange-600 bg-orange-100 dark:bg-orange-900/30 dark:text-orange-400",
    purple: "text-purple-600 bg-purple-100 dark:bg-purple-900/30 dark:text-purple-400",
    red: "text-red-600 bg-red-100 dark:bg-red-900/30 dark:text-red-400",
  };
  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-muted-foreground mb-1">{title}</p>
          <p className="text-2xl font-bold">{value}</p>
          {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
        </div>
        <div className={`p-2.5 rounded-lg ${colors[color]}`}><Icon size={20} /></div>
      </div>
    </div>
  );
};

// ─── Tab: Tableau de bord ────────────────────────────────────────────────────

const DashboardTab = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get("v1/benefits/dashboard").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;
  if (!data) return null;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard title={t("benefitsHub.dashboard.kpiActiveEnrollments")} value={data.total_active_enrollments} icon={CheckCircle} color="green" />
        <KpiCard title={t("benefitsHub.dashboard.kpiCoveredEmployees")} value={`${data.coverage_pct}%`} sub={t("benefitsHub.dashboard.coveredSub", { count: data.covered_employees })} icon={Users} color="blue" />
        <KpiCard title={t("benefitsHub.dashboard.kpiMonthlyEmployerCost")} value={`${fmt(data.monthly_employer_cost)} ${data.currency || ""}`} icon={Euro} color="orange" />
        <KpiCard title={t("benefitsHub.dashboard.kpiAnnualTotalCost")} value={`${fmt(data.annual_employer_cost)} ${data.currency || ""}`} sub={t("benefitsHub.dashboard.employerShare")} icon={TrendingUp} color="purple" />
      </div>

      {/* Coverage bar */}
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">{t("benefitsHub.dashboard.coverageTitle")}</h3>
          <span className="text-2xl font-bold text-primary">{data.coverage_pct}%</span>
        </div>
        <div className="h-3 rounded-full bg-muted">
          <div className="h-3 rounded-full bg-primary transition-all" style={{ width: `${data.coverage_pct}%` }} />
        </div>
        <p className="text-xs text-muted-foreground mt-2">{t("benefitsHub.dashboard.coverageLine", { count: data.covered_employees })}</p>
      </div>

      {/* By category */}
      {data.enrollments_by_category?.length > 0 && (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-border">
            <h3 className="font-semibold flex items-center gap-2"><BarChart3 size={16} /> {t("benefitsHub.dashboard.enrollmentsByCategory")}</h3>
          </div>
          <div className="divide-y divide-border">
            {data.enrollments_by_category.map((c) => (
              <div key={c.category} className="px-5 py-3 flex items-center gap-4">
                <CategoryIcon category={c.category} />
                <div className="flex-1">
                  <p className="font-medium text-sm">{categoryLabel(c.category, t, c.label)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-24 h-2 rounded-full bg-muted">
                    <div className="h-2 rounded-full bg-primary" style={{ width: `${Math.min(100, (c.count / data.total_active_enrollments) * 100)}%` }} />
                  </div>
                  <span className="text-sm font-semibold w-8 text-right">{c.count}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Cost by category */}
      {Object.keys(data.cost_by_category || {}).length > 0 && (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-border">
            <h3 className="font-semibold flex items-center gap-2"><Euro size={16} /> {t("benefitsHub.dashboard.monthlyCostByCategory")}</h3>
          </div>
          <div className="divide-y divide-border">
            {Object.entries(data.cost_by_category).sort(([, a], [, b]) => b - a).map(([cat, cost]) => (
              <div key={cat} className="px-5 py-3 flex items-center justify-between">
                <span className="text-sm font-medium">{categoryLabel(cat, t)}</span>
                <span className="text-sm font-semibold">{t("benefitsHub.common.perMonthAmount", { amount: fmt(cost), currency: data.currency || "" })}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Tab: Plans ───────────────────────────────────────────────────────────────

const CreatePlanModal = ({ onClose, onCreated, defaultCountry }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: "", category: "health", employer_contribution: "", employee_contribution: "",
    provider: "", is_mandatory: false, country: defaultCountry || "TN",
  });
  const currency = COUNTRY_CURRENCY[form.country] || "";
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      await apiClient.post("v1/benefits/plans", {
        ...form,
        employer_contribution: parseFloat(form.employer_contribution) || 0,
        employee_contribution: parseFloat(form.employee_contribution) || 0,
      });
      onCreated();
      onClose();
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="font-semibold text-lg">{t("benefitsHub.plans.createTitle")}</h2>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">{t("benefitsHub.plans.fieldName")}</label>
            <input className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm"
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("benefitsHub.plans.namePlaceholder")} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">{t("benefitsHub.plans.fieldCategory")}</label>
              <select className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm"
                value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{categoryLabel(c.value, t)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t("benefitsHub.plans.fieldCountry")}</label>
              <select className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm"
                value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })}>
                <option value="FR">{t("benefitsHub.countries.FR")}</option>
                <option value="TN">{t("benefitsHub.countries.TN")}</option>
                <option value="QA">{t("benefitsHub.countries.QA")}</option>
                <option value="MA">{t("benefitsHub.countries.MA")}</option>
                <option value="SA">{t("benefitsHub.countries.SA")}</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t("benefitsHub.plans.fieldProvider")}</label>
            <input className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm"
              value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} placeholder={t("benefitsHub.plans.providerPlaceholder")} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">{t("benefitsHub.plans.fieldEmployerShare", { currency })}</label>
              <input type="number" step="0.01" className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm"
                value={form.employer_contribution} onChange={(e) => setForm({ ...form, employer_contribution: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t("benefitsHub.plans.fieldEmployeeShare", { currency })}</label>
              <input type="number" step="0.01" className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm"
                value={form.employee_contribution} onChange={(e) => setForm({ ...form, employee_contribution: e.target.value })} />
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.is_mandatory} onChange={(e) => setForm({ ...form, is_mandatory: e.target.checked })} className="rounded" />
            <span className="text-sm">{t("benefitsHub.plans.mandatoryBenefit")}</span>
          </label>
        </div>
        <div className="px-6 py-4 border-t border-border flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm border border-border hover:bg-muted">{t("benefitsHub.common.cancel")}</button>
          <button onClick={save} disabled={saving || !form.name.trim()}
            className="px-4 py-2 rounded-lg text-sm bg-primary text-primary-foreground disabled:opacity-50">
            {saving ? t("benefitsHub.plans.creating") : t("benefitsHub.plans.createCta")}
          </button>
        </div>
      </div>
    </div>
  );
};

const PlansTab = () => {
  const { t } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [defaultCountry, setDefaultCountry] = useState("");
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    apiClient.get("v1/benefits/plans").then((r) => {
      setPlans(r.data.items || []);
      setDefaultCountry(r.data.default_country || "");
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const deletePlan = async (id) => {
    if (!window.confirm(t("benefitsHub.plans.confirmDelete"))) return;
    await apiClient.delete(`v1/benefits/plans/${id}`);
    load();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{t("benefitsHub.plans.count", { count: plans.length })}</p>
        <div className="flex gap-2">
          <button onClick={load} className="p-2 text-muted-foreground hover:text-foreground"><RefreshCw size={14} /></button>
          <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium">
            <Plus size={15} /> {t("benefitsHub.plans.new")}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>
      ) : plans.length === 0 ? (
        <div className="text-center py-16 bg-card border border-border rounded-xl">
          <Gift size={40} className="mx-auto text-muted-foreground mb-3" />
          <p className="font-medium">{t("benefitsHub.plans.empty")}</p>
          <p className="text-sm text-muted-foreground mt-1">{t("benefitsHub.plans.emptyHint")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {plans.map((p) => (
            <div key={p.id} className="bg-card border border-border rounded-xl p-5 space-y-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <CategoryIcon category={p.category} size={18} />
                  <div>
                    <p className="font-semibold leading-tight">{p.name}</p>
                    <p className="text-xs text-muted-foreground">{categoryLabel(p.category, t, p.category_label)}</p>
                  </div>
                </div>
                <button onClick={() => deletePlan(p.id)} className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-muted-foreground hover:text-red-500">
                  <Trash2 size={13} />
                </button>
              </div>
              {p.provider && <p className="text-xs text-muted-foreground">{p.provider}</p>}
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="bg-muted/40 rounded-lg p-2 text-center">
                  <p className="font-bold text-sm">{fmt(p.employer_contribution)} {p.currency}</p>
                  <p className="text-muted-foreground">{t("benefitsHub.plans.employerPerMonth")}</p>
                </div>
                <div className="bg-muted/40 rounded-lg p-2 text-center">
                  <p className="font-bold text-sm">{fmt(p.employee_contribution)} {p.currency}</p>
                  <p className="text-muted-foreground">{t("benefitsHub.plans.employeePerMonth")}</p>
                </div>
              </div>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{t("benefitsHub.plans.enrolledCount", { count: p.enrollment_count })}</span>
                <div className="flex gap-1">
                  {p.is_mandatory && <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 font-medium">{t("benefitsHub.plans.mandatory")}</span>}
                  <span className="px-2 py-0.5 rounded-full bg-muted font-medium">{p.country}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && <CreatePlanModal onClose={() => setShowCreate(false)} onCreated={load} defaultCountry={defaultCountry} />}
    </div>
  );
};

// ─── Tab: Inscriptions ────────────────────────────────────────────────────────

const EnrollModal = ({ plans, onClose, onEnrolled }) => {
  const { t } = useTranslation();
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({ plan_id: "", employee_id: "", enrollment_date: new Date().toISOString().slice(0, 10) });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiClient.get("v1/employees/?limit=200").then((r) => setEmployees(r.data.employees || r.data.items || []));
  }, []);

  const save = async () => {
    if (!form.plan_id || !form.employee_id) return;
    setSaving(true);
    try {
      await apiClient.post(`v1/benefits/plans/${form.plan_id}/enroll`, { employee_id: parseInt(form.employee_id), enrollment_date: form.enrollment_date });
      onEnrolled();
      onClose();
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="font-semibold text-lg">{t("benefitsHub.enrollments.enrollTitle")}</h2>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">{t("benefitsHub.enrollments.fieldPlan")}</label>
            <select className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm"
              value={form.plan_id} onChange={(e) => setForm({ ...form, plan_id: e.target.value })}>
              <option value="">{t("benefitsHub.common.select")}</option>
              {plans.map((p) => <option key={p.id} value={p.id}>{p.name} ({categoryLabel(p.category, t, p.category_label)})</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t("benefitsHub.enrollments.fieldEmployee")}</label>
            <select className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm"
              value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })}>
              <option value="">{t("benefitsHub.common.select")}</option>
              {employees.map((e) => <option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t("benefitsHub.enrollments.fieldEnrollmentDate")}</label>
            <input type="date" className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm"
              value={form.enrollment_date} onChange={(e) => setForm({ ...form, enrollment_date: e.target.value })} />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-border flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm border border-border hover:bg-muted">{t("benefitsHub.common.cancel")}</button>
          <button onClick={save} disabled={saving || !form.plan_id || !form.employee_id}
            className="px-4 py-2 rounded-lg text-sm bg-primary text-primary-foreground disabled:opacity-50">
            {saving ? t("benefitsHub.enrollments.enrolling") : t("benefitsHub.enrollments.enrollCta")}
          </button>
        </div>
      </div>
    </div>
  );
};

const EnrollmentsTab = () => {
  const { t } = useTranslation();
  const [enrollments, setEnrollments] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("active");
  const [showEnroll, setShowEnroll] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      apiClient.get(`v1/benefits/enrollments?status=${statusFilter}`),
      apiClient.get("v1/benefits/plans"),
    ]).then(([r1, r2]) => {
      setEnrollments(r1.data.items || []);
      setPlans(r2.data.items || []);
    }).finally(() => setLoading(false));
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const terminate = async (id) => {
    if (!window.confirm(t("benefitsHub.enrollments.confirmTerminate"))) return;
    await apiClient.patch(`v1/benefits/enrollments/${id}/terminate`, {});
    load();
  };

  const statusLabel = (status) => t(`benefitsHub.enrollments.status.${status}`, { defaultValue: status });
  const STATUS_COLORS = {
    active: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
    suspended: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
    terminated: "bg-gray-100 text-gray-600 dark:bg-gray-800",
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-2">
          {["active", "suspended", "terminated"].map((s) => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${statusFilter === s ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}>
              {statusLabel(s)}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="p-2 text-muted-foreground hover:text-foreground"><RefreshCw size={14} /></button>
          <button onClick={() => setShowEnroll(true)} className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium">
            <Plus size={15} /> {t("benefitsHub.enrollments.enroll")}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>
      ) : (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/30">
                <tr>
                  <th className="text-left px-5 py-3 font-medium text-muted-foreground">{t("benefitsHub.enrollments.colEmployee")}</th>
                  <th className="text-left px-5 py-3 font-medium text-muted-foreground">{t("benefitsHub.enrollments.colPlan")}</th>
                  <th className="text-left px-5 py-3 font-medium text-muted-foreground">{t("benefitsHub.enrollments.colStatus")}</th>
                  <th className="text-right px-5 py-3 font-medium text-muted-foreground">{t("benefitsHub.enrollments.colEmployer")}</th>
                  <th className="text-right px-5 py-3 font-medium text-muted-foreground">{t("benefitsHub.enrollments.colEmployeeShare")}</th>
                  <th className="text-left px-5 py-3 font-medium text-muted-foreground">{t("benefitsHub.enrollments.colSince")}</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {enrollments.map((e) => (
                  <tr key={e.id} className="hover:bg-muted/20">
                    <td className="px-5 py-3">
                      <p className="font-medium">{e.employee_name}</p>
                      <p className="text-xs text-muted-foreground">{e.department}</p>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <CategoryIcon category={e.plan_category} size={13} />
                        <span>{e.plan_name}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[e.status]}`}>
                        {statusLabel(e.status)}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right font-medium">{fmt(e.employer_contribution)} {e.currency}</td>
                    <td className="px-5 py-3 text-right text-muted-foreground">{fmt(e.employee_contribution)} {e.currency}</td>
                    <td className="px-5 py-3 text-muted-foreground">{fmtDate(e.enrollment_date)}</td>
                    <td className="px-5 py-3">
                      {e.status === "active" && (
                        <button onClick={() => terminate(e.id)} className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-muted-foreground hover:text-red-500">
                          <Trash2 size={13} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {enrollments.length === 0 && (
                  <tr><td colSpan={7} className="text-center py-12 text-muted-foreground">{t("benefitsHub.enrollments.empty")}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showEnroll && <EnrollModal plans={plans} onClose={() => setShowEnroll(false)} onEnrolled={load} />}
    </div>
  );
};

// ─── Tab: Analytics ───────────────────────────────────────────────────────────

const AnalyticsTab = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get("v1/benefits/analytics/costs").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;
  if (!data?.plans?.length) return <div className="text-center py-16 text-muted-foreground">{t("benefitsHub.analytics.empty")}</div>;

  const maxCost = Math.max(1, ...data.plans.map((p) => p.monthly_total));

  return (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-border">
          <h3 className="font-semibold flex items-center gap-2"><Euro size={16} /> {t("benefitsHub.analytics.monthlyCostByPlan")}</h3>
        </div>
        <div className="divide-y divide-border">
          {data.plans.map((p) => (
            <div key={p.plan_id} className="px-5 py-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="font-medium">{p.plan_name}</p>
                  <p className="text-xs text-muted-foreground">{categoryLabel(p.category, t, p.category_label)} · {t("benefitsHub.plans.enrolledCount", { count: p.enrollment_count })}</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold">{t("benefitsHub.common.perMonthAmount", { amount: fmt(p.monthly_total), currency: p.currency || "" })}</p>
                  <p className="text-xs text-muted-foreground">{t("benefitsHub.common.perYearAmount", { amount: fmt(p.annual_total), currency: p.currency || "" })}</p>
                </div>
              </div>
              <div className="h-2 rounded-full bg-muted">
                <div className="h-2 rounded-full bg-primary" style={{ width: `${(p.monthly_total / maxCost) * 100}%` }} />
              </div>
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span>{t("benefitsHub.analytics.employerAmount", { amount: fmt(p.monthly_employer_total), currency: p.currency || "" })}</span>
                <span>{t("benefitsHub.analytics.employeeAmount", { amount: fmt(p.monthly_employee_total), currency: p.currency || "" })}</span>
              </div>
            </div>
          ))}
        </div>
        <div className="px-5 py-4 border-t border-border bg-muted/20 flex items-center justify-between">
          <span className="font-medium text-sm">{t("benefitsHub.analytics.monthlyTotal")}</span>
          <span className="font-bold">{fmt(data.plans.reduce((s, p) => s + p.monthly_total, 0))} {data.currency || ""}</span>
        </div>
      </div>
    </div>
  );
};

// ─── Hub ─────────────────────────────────────────────────────────────────────

const TABS = [
  { id: "dashboard", icon: BarChart3 },
  { id: "plans", icon: Gift },
  { id: "enrollments", icon: Users },
  { id: "analytics", icon: Euro },
];

export default function BenefitsHub() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "dashboard";
  const setTab = (id) => setSearchParams({ tab: id });

  const renderContent = () => {
    switch (activeTab) {
      case "plans": return <PlansTab />;
      case "enrollments": return <EnrollmentsTab />;
      case "analytics": return <AnalyticsTab />;
      default: return <DashboardTab />;
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Gift size={24} className="text-primary" />
            {t("benefitsHub.title")}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t("benefitsHub.subtitle")}
          </p>
        </div>
      </div>

      <div className="flex gap-1 bg-muted/50 rounded-xl p-1 w-fit flex-wrap">
        {TABS.map(({ id, icon: Icon }) => (
          <button key={id} onClick={() => setTab(id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === id ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
            <Icon size={15} />
            {t(`benefitsHub.tabs.${id}`)}
          </button>
        ))}
      </div>

      {renderContent()}
    </div>
  );
}
